import axios from "axios";
import { BASE_URL } from "@/config";
import router from "@/router";

const TOKEN_STORAGE_KEY = "bt_refresh_token";

let accessToken = null;
let refreshPromise = null;

function getStoredRefreshToken() {
  return localStorage.getItem(TOKEN_STORAGE_KEY);
}

export function setTokens(access, refresh) {
  accessToken = access;
  if (refresh) {
    localStorage.setItem(TOKEN_STORAGE_KEY, refresh);
  }
}

export function clearTokens() {
  accessToken = null;
  localStorage.removeItem(TOKEN_STORAGE_KEY);
}

export function getAccessToken() {
  return accessToken;
}

export async function refreshAccessToken() {
  if (refreshPromise) return refreshPromise;
  const refreshToken = getStoredRefreshToken();
  if (!refreshToken) return false; // nothing to restore — app client is token-only

  refreshPromise = (async () => {
    try {
      // allauth headless (app client): POST /_allauth/app/v1/tokens/refresh
      // -> 200 {"status": 200, "data": {"access_token", "refresh_token"}}
      // Tokens are single-use (rotated) — the new refresh token must be stored.
      const response = await axios.post(
        `${BASE_URL}/_allauth/app/v1/tokens/refresh`,
        { refresh_token: refreshToken },
        {
          withCredentials: true,
          headers: { "Content-Type": "application/json" },
        },
      );

      const payload = response.data?.data ?? {};
      const access = payload.access_token ?? response.data?.meta?.access_token;
      const refresh =
        payload.refresh_token ?? response.data?.meta?.refresh_token;
      if (!access) return false;
      setTokens(access, refresh); // keeps the old refresh token if none returned
      return true;
    } catch {
      clearTokens();
      return false;
    } finally {
      refreshPromise = null;
    }
  })();
  return refreshPromise;
}

export const apiClient = axios.create({
  baseURL: BASE_URL,
  withCredentials: true,
  headers: {
    "Content-Type": "application/json",
    "X-Client-Type": "app",
  },
});

apiClient.interceptors.request.use((config) => {
  if (accessToken) {
    config.headers.Authorization = `Bearer ${accessToken}`;
  }
  return config;
});

apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (error.response?.status === 403 && error.response?.data?.demo) {
      import("@/stores/main").then(({ useMainStore }) => {
        const store = useMainStore();
        store.demoModalVisible = true;
      });
      return Promise.resolve({ data: null, _demoblocked: true });
    }
    const originalRequest = error.config;
    if (
      error.response?.status === 401 &&
      !originalRequest._retried &&
      !originalRequest.url?.includes("/_allauth/")
    ) {
      originalRequest._retried = true;
      const refreshed = await refreshAccessToken();
      if (refreshed) {
        originalRequest.headers.Authorization = `Bearer ${accessToken}`;
        return apiClient(originalRequest);
      }
      const route = router.currentRoute.value;
      if (route.meta?.requiresAuth) {
        import("@/stores/main").then(({ useMainStore }) => {
          useMainStore().setUser(null);
          if (router.currentRoute.value.path !== "/login") {
            router.push("/login");
          }
        });
        return new Promise(() => {});
      }
    }
    return Promise.reject(error);
  },
);

export class Demo {
  static async getIndustries() {
    const response = await apiClient.get("/api/demo/industries");
    return response.data;
  }

  static async start(industry) {
    const response = await apiClient.post(
      `/api/demo/start?industry=${industry}`,
    );
    // The API authenticates by JWT; without these the demo bounces to /login.
    if (response.data?.access_token) {
      setTokens(response.data.access_token, response.data.refresh_token);
    }
    return response.data;
  }
}

export class Auth {
  static async login(email, password) {
    const response = await apiClient.post("/_allauth/app/v1/auth/login", {
      email,
      password,
    });
    const meta = response.data?.meta;
    if (meta?.access_token) {
      setTokens(meta.access_token, meta.refresh_token);
    }
    return response.data;
  }

  static async signup(email, password) {
    const response = await apiClient.post("/_allauth/app/v1/auth/signup", {
      email,
      password,
    });
    const meta = response.data?.meta;
    if (meta?.access_token) {
      setTokens(meta.access_token, meta.refresh_token);
    }
    return response.data;
  }

  static async logout() {
    try {
      const response = await apiClient.delete("/_allauth/app/v1/auth/session");
      return response.data;
    } finally {
      clearTokens();
    }
  }

  static async requestPasswordReset(email) {
    const response = await apiClient.post(
      "/_allauth/app/v1/auth/password/request",
      {
        email,
      },
    );
    return response.data;
  }

  static async confirmPasswordReset(key, password) {
    const response = await apiClient.post(
      "/_allauth/app/v1/auth/password/reset",
      {
        key,
        password,
      },
    );
    return response.data;
  }

  static googleLoginUrl() {
    return `${BASE_URL}/accounts/google/login/?process=login&next=${encodeURIComponent(`${window.location.origin}/auth/callback`)}`;
  }
}

export class Review {
  static async getReviewsByCompany(
    companyID,
    locationID,
    dateFrom,
    dateTo,
    page,
    perPage,
    sentiment,
    source,
    topic,
    emotion,
    replied = null,
    sort = null,
    q = null,
  ) {
    if (typeof companyID === "string") {
      companyID = parseInt(companyID);
    }
    if (typeof locationID === "string") {
      locationID = parseInt(locationID);
    }

    try {
      const response = await apiClient.post(`/api/company/reviews`, {
        company_id: companyID,
        location_id: locationID,
        date_from: dateFrom,
        date_to: dateTo,
        page: page,
        per_page: perPage,
        sentiment: sentiment,
        source: source,
        topic: topic,
        emotion: emotion,
        replied: replied,
        sort: sort,
        q: q,
      });
      return response.data;
    } catch (error) {
      console.error("Error fetching reviews:", error);
      return [];
    }
  }

  // The five tab-badge totals in one request. Replaces the old five per_page=1
  // probe calls; returns all-zero badges on failure so a hiccup blanks the
  // numbers rather than breaking the screen.
  static async getReviewCounts(
    companyID,
    locationID,
    dateFrom,
    dateTo,
    source = null,
    topic = null,
    q = null,
  ) {
    if (typeof companyID === "string") companyID = parseInt(companyID);
    if (typeof locationID === "string") locationID = parseInt(locationID);
    try {
      const response = await apiClient.post(`/api/company/review-counts`, {
        company_id: companyID,
        location_id: locationID,
        date_from: dateFrom,
        date_to: dateTo,
        source: source,
        topic: topic,
        q: q,
      });
      return response.data;
    } catch (error) {
      console.error("Error fetching review counts:", error);
      return { all: 0, negative: 0, positive: 0, replied: 0, needs: 0 };
    }
  }

  static async getRecentReviews(companyId, dateFrom, dateTo) {
    if (typeof companyId === "string") {
      companyId = parseInt(companyId);
    }

    try {
      const response = await apiClient.post(`/api/company/recent-reviews`, {
        company_id: companyId,
        date_from: dateFrom,
        date_to: dateTo,
      });
      return response.data;
    } catch (error) {
      console.error("Error fetching recent reviews:", error);
      return [];
    }
  }

  static async getStatsByCompanyIDs(companyIds, dateFrom, dateTo) {
    try {
      const response = await apiClient.post(`/api/company/review-stats`, {
        company_ids: companyIds,
        date_from: dateFrom,
        date_to: dateTo,
      });
      return response.data;
    } catch (error) {
      console.error("Error fetching review stats:", error);
      return [];
    }
  }

  static async getBranchComparison(companyId, dateFrom, dateTo) {
    try {
      const response = await apiClient.post(`/api/company/branch-comparison`, {
        company_id: companyId,
        date_from: dateFrom,
        date_to: dateTo,
      });
      return response.data;
    } catch (error) {
      console.error("Error fetching branch comparison:", error);
      return {
        branches: [],
        company_wide: null,
        unassigned: { channel_count: 0, review_count: 0 },
      };
    }
  }

  static async getOverviewStatsByCompanyID(
    companyID,
    dateFrom,
    dateTo,
    locationId = null,
  ) {
    try {
      const response = await apiClient.post(`/api/overview/review-stats`, {
        company_id: companyID,
        date_from: dateFrom,
        date_to: dateTo,
        location_id: locationId,
      });
      return response.data;
    } catch (error) {
      console.error("Error fetching review stats:", error);
      return [];
    }
  }

  static async getNegativeTopics(
    companyID,
    dateFrom,
    dateTo,
    locationId = null,
  ) {
    if (typeof companyID === "string") {
      companyID = parseInt(companyID);
    }

    try {
      const response = await apiClient.post(`/api/review/topics-negative`, {
        company_id: companyID,
        date_from: dateFrom,
        date_to: dateTo,
        location_id: locationId,
      });
      return response.data;
    } catch (error) {
      console.error("Error fetching negative topics:", error);
      return [];
    }
  }

  static async getPositiveTopics(
    companyID,
    dateFrom,
    dateTo,
    locationId = null,
  ) {
    if (typeof companyID === "string") {
      companyID = parseInt(companyID);
    }

    try {
      const response = await apiClient.post(`/api/review/topics-positive`, {
        company_id: companyID,
        date_from: dateFrom,
        date_to: dateTo,
        location_id: locationId,
      });
      return response.data;
    } catch (error) {
      console.error("Error fetching positive topics:", error);
      return [];
    }
  }

  static async getTopEmotions(companyID, dateFrom, dateTo) {
    if (typeof companyID === "string") {
      companyID = parseInt(companyID);
    }

    try {
      const response = await apiClient.post(`/api/review/top-emotions`, {
        company_id: companyID,
        date_from: dateFrom,
        date_to: dateTo,
      });
      return response.data;
    } catch (error) {
      console.error("Error fetching top emotions:", error);
      return [];
    }
  }

  static async getSentimentBySource(
    companyID,
    dateFrom,
    dateTo,
    locationId = null,
  ) {
    if (typeof companyID === "string") {
      companyID = parseInt(companyID);
    }

    try {
      const response = await apiClient.post(`/api/review/sentiment-by-source`, {
        company_id: companyID,
        date_from: dateFrom,
        date_to: dateTo,
        location_id: locationId,
      });
      return response.data;
    } catch (error) {
      console.error("Error fetching sentiment by source:", error);
      return [];
    }
  }

  static async getPercentageNegative(companyID, aggregateBy, dateFrom, dateTo) {
    if (typeof companyID === "string") {
      companyID = parseInt(companyID);
    }

    try {
      const response = await apiClient.post(`/api/review/percentage-negative`, {
        company_id: companyID,
        aggregate_by: aggregateBy,
        date_from: dateFrom,
        date_to: dateTo,
      });
      return response.data;
    } catch (error) {
      console.error("Error fetching negative percentage:", error);
      return null;
    }
  }

  static async getTopicsByCompany(
    companyID,
    page = 1,
    perPage = 10,
    sentiment = null,
  ) {
    if (typeof companyID === "string") {
      companyID = parseInt(companyID);
    }

    try {
      const response = await apiClient.post(`/api/company/topics`, {
        company_id: companyID,
        page: page,
        per_page: perPage,
        sentiment: sentiment,
      });
      return response.data;
    } catch (error) {
      console.error("Error fetching company topics:", error);
      return [];
    }
  }

  static async getCountByPlatformByMonth(
    companyID,
    aggregateBy,
    dateFrom,
    dateTo,
    locationId = null,
  ) {
    if (typeof companyID === "string") {
      companyID = parseInt(companyID);
    }

    try {
      const response = await apiClient.post(
        `/api/review/count-by-platform-by-month`,
        {
          company_id: companyID,
          aggregate_by: aggregateBy,
          date_from: dateFrom,
          date_to: dateTo,
          location_id: locationId,
        },
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching review count by platform by month:", error);
      return null;
    }
  }

  static async getTopicTrend(companyID, topic, aggregateBy, dateFrom, dateTo) {
    if (typeof companyID === "string") {
      companyID = parseInt(companyID);
    }

    try {
      const response = await apiClient.post(`/api/review/topic-trend`, {
        company_id: companyID,
        topic: topic,
        aggregate_by: aggregateBy,
        date_from: dateFrom,
        date_to: dateTo,
      });
      return response.data;
    } catch (error) {
      console.error("Error fetching topic trend:", error);
      return null;
    }
  }

  static async getCompetitorTrend(companyIds, aggregateBy, dateFrom, dateTo) {
    try {
      const response = await apiClient.post(`/api/company/competitor-trend`, {
        company_ids: companyIds,
        aggregate_by: aggregateBy,
        date_from: dateFrom,
        date_to: dateTo,
      });
      return response.data;
    } catch (error) {
      console.error("Error fetching competitor trend:", error);
      return { dates: [], companies: [] };
    }
  }

  static async getTopicsOverview(companyID, aggregateBy, dateFrom, dateTo) {
    if (typeof companyID === "string") {
      companyID = parseInt(companyID);
    }

    try {
      const response = await apiClient.post(`/api/review/topics-overview`, {
        company_id: companyID,
        aggregate_by: aggregateBy,
        date_from: dateFrom,
        date_to: dateTo,
      });
      return response.data;
    } catch (error) {
      console.error("Error fetching topics overview:", error);
      return { items: [], total: 0 };
    }
  }
}

export class Dashboard {
  static async getReviewTrend(
    companyId,
    period,
    dateFrom,
    dateTo,
    locationId = null,
  ) {
    try {
      const response = await apiClient.post(`/api/dashboard/review-trend`, {
        company_id: companyId,
        period: period,
        date_from: dateFrom,
        date_to: dateTo,
        location_id: locationId,
      });
      return response.data;
    } catch (error) {
      console.error("Error fetching review trend:", error);
      return null;
    }
  }

  static async getReputationScore(
    companyId,
    dateFrom,
    dateTo,
    locationId = null,
  ) {
    try {
      const response = await apiClient.post(`/api/company/reputation-score`, {
        company_id: companyId,
        date_from: dateFrom,
        date_to: dateTo,
        location_id: locationId,
      });
      return response.data;
    } catch (error) {
      console.error("Error fetching reputation score:", error);
      return null;
    }
  }

  static async getPriorityReviews(
    companyId,
    dateFrom,
    dateTo,
    limit = 10,
    locationId = null,
    q = null,
    source = null,
    topic = null,
  ) {
    try {
      const response = await apiClient.post(`/api/company/priority-reviews`, {
        company_id: companyId,
        date_from: dateFrom,
        date_to: dateTo,
        limit: limit,
        location_id: locationId,
        q: q,
        source: source,
        topic: topic,
      });
      return response.data;
    } catch (error) {
      console.error("Error fetching priority reviews:", error);
      return { count: 0, items: [] };
    }
  }
}

export class User {
  static async getUser() {
    try {
      const response = await apiClient.get("/api/user");
      return response.data;
    } catch (error) {
      // 401 just means "not logged in" — leave the store unauthenticated and
      // let the router guard send protected routes to /login. Redirecting here
      // would wrongly bounce logged-out users off PUBLIC pages (password reset,
      // invite, forgot-password) when opened fresh (e.g. from an email link).
      if (error.response?.status !== 401) {
        console.error("Error fetching user:", error);
      }
      return null;
    }
  }
}

export class Channel {
  static async getTrackedChannels() {
    try {
      const response = await apiClient.get("/api/channel/tracked-channels");
      return response.data;
    } catch (error) {
      console.error("Error fetching tracked channels:", error);
      return [];
    }
  }

  static async createChannel(
    companyId,
    name,
    reviewSources,
    address = "",
    locality = "",
  ) {
    const response = await apiClient.post("/api/channels", {
      company_id: companyId,
      name,
      review_sources: reviewSources,
      address,
      locality,
    });
    return response.data;
  }

  static async getCompanyChannels(companyId) {
    try {
      const response = await apiClient.get("/api/company/channels", {
        params: { company_id: companyId },
      });
      return response.data;
    } catch (error) {
      console.error("Error fetching company channels:", error);
      return { channels: [] };
    }
  }

  static async updateChannel(channelId, data) {
    const response = await apiClient.patch(`/api/channels/${channelId}`, data);
    return response.data;
  }

  static async deleteChannel(channelId) {
    const response = await apiClient.delete(`/api/channels/${channelId}`);
    return response.data;
  }

  static async searchChannels(source, q) {
    const response = await apiClient.get("/api/channels/search", {
      params: { source, q },
    });
    return response.data.results || [];
  }

  static async getCompanyBranches(companyId) {
    try {
      const response = await apiClient.get("/api/company/branches", {
        params: { company_id: companyId },
      });
      return response.data.branches || [];
    } catch (error) {
      console.error("Error fetching branches:", error);
      return [];
    }
  }

  static async createBranch(companyId, name) {
    const response = await apiClient.post("/api/company/branches", {
      company_id: companyId,
      name,
    });
    return response.data;
  }

  static async renameBranch(branchId, name) {
    const response = await apiClient.patch(`/api/branches/${branchId}`, {
      name,
    });
    return response.data;
  }

  static async deleteBranch(branchId) {
    const response = await apiClient.delete(`/api/branches/${branchId}`);
    return response.data;
  }

  static async mergeBranch(branchId, targetId) {
    const response = await apiClient.post(`/api/branches/${branchId}/merge`, {
      target_id: targetId,
    });
    return response.data;
  }

  static async reclusterBranches(companyId) {
    const response = await apiClient.post("/api/company/branches/recluster", {
      company_id: companyId,
    });
    return response.data;
  }
}

export class Competitor {
  static async research(ownerCompanyId, name, website = "") {
    const res = await apiClient.post("/api/company/competitors/research", {
      owner_company_id: ownerCompanyId,
      name,
      website,
    });
    return res.data;
  }
  static async create(ownerCompanyId, name, website = "", channels = []) {
    const res = await apiClient.post("/api/company/competitors", {
      owner_company_id: ownerCompanyId,
      name,
      website,
      channels,
    });
    return res.data;
  }
  static async remove(linkId) {
    const res = await apiClient.delete(`/api/company/competitors/${linkId}`);
    return res.data;
  }
  static async promote(linkId) {
    const res = await apiClient.post(
      `/api/company/competitors/${linkId}/reclassify`,
      {
        action: "promote",
      },
    );
    return res.data;
  }
}

export class Organisation {
  static async getCompaniesWatched() {
    // Gets organisation from session, returns companies watched by organisation
    try {
      const response = await apiClient.get(
        "/api/organisation/companies-watched",
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching company list:", error);
      return [];
    }
  }

  static async getInfo(organisationIds) {
    try {
      const response = await apiClient.post("/api/organisation/info", {
        organisation_ids: organisationIds,
      });
      return response.data;
    } catch (error) {
      console.error("Error fetching organisation info:", error);
      return [];
    }
  }

  static async select(organisationId) {
    try {
      const response = await apiClient.post(
        `/api/organisation/select?organisation_id=${organisationId}`,
      );
      return response.data;
    } catch (error) {
      console.error("Error selecting organisation:", error);
      throw error;
    }
  }

  static async createInvite(organisationId, email, name = "") {
    try {
      const response = await apiClient.post(`/api/organisation/invite/create`, {
        organisation_id: organisationId,
        email: email,
        name: name,
      });
      return response.data;
    } catch (error) {
      console.error("Error creating organisation invite:", error);
      throw error;
    }
  }

  static async changeMemberRole(organisationId, userId, role) {
    const response = await apiClient.post(`/api/organisation/member/role`, {
      organisation_id: organisationId,
      user_id: userId,
      role: role,
    });
    return response.data;
  }

  static async removeMember(organisationId, userId) {
    const response = await apiClient.post(`/api/organisation/member/remove`, {
      organisation_id: organisationId,
      user_id: userId,
    });
    return response.data;
  }

  static async getMembers(organisationId) {
    try {
      const response = await apiClient.get(
        `/api/organisation/members?organisation_id=${organisationId}`,
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching members:", error);
      return [];
    }
  }

  static async getPendingInvites(organisationId) {
    try {
      const response = await apiClient.get(
        `/api/organisation/invites?organisation_id=${organisationId}`,
      );
      return response.data;
    } catch (error) {
      console.error("Error fetching pending invites:", error);
      return [];
    }
  }

  static async revokeInvite(inviteId) {
    const response = await apiClient.post(`/api/organisation/invite/revoke`, {
      invite_id: inviteId,
    });
    return response.data;
  }

  static async getInvite(token) {
    const response = await apiClient.get(`/api/invite/${token}`);
    return response.data;
  }

  static async acceptInvite(token) {
    const response = await apiClient.post(`/api/invite/${token}/accept`);
    return response.data;
  }

  static async declineInvite(token) {
    const response = await apiClient.post(`/api/invite/${token}/decline`);
    return response.data;
  }
}

export class Topic {
  static async create(companyId, label, sentiment) {
    const response = await apiClient.post("/api/topic", {
      company_id: companyId,
      label,
      sentiment,
    });
    return response.data;
  }

  static async update(topicId, companyId, label, sentiment) {
    const response = await apiClient.patch(`/api/topic/${topicId}`, {
      company_id: companyId,
      label,
      sentiment,
    });
    return response.data;
  }
}

export class Entity {
  static async list(
    companyId,
    type,
    dateFrom = null,
    dateTo = null,
    locationId = null,
  ) {
    const response = await apiClient.post("/api/entities", {
      company_id: companyId,
      type,
      date_from: dateFrom,
      date_to: dateTo,
      location_id: locationId,
    });
    return response.data;
  }

  static async trend(
    companyId,
    type,
    label,
    aggregateBy = "monthly",
    dateFrom = null,
    dateTo = null,
    locationId = null,
  ) {
    const response = await apiClient.post("/api/entities/trend", {
      company_id: companyId,
      type,
      label,
      aggregate_by: aggregateBy,
      date_from: dateFrom,
      date_to: dateTo,
      location_id: locationId,
    });
    return response.data;
  }

  static async reviews(
    companyId,
    type,
    label,
    page = 1,
    perPage = 5,
    dateFrom = null,
    dateTo = null,
    locationId = null,
  ) {
    const response = await apiClient.post("/api/entities/reviews", {
      company_id: companyId,
      type,
      label,
      page,
      per_page: perPage,
      date_from: dateFrom,
      date_to: dateTo,
      location_id: locationId,
    });
    return response.data;
  }

  static async overview(
    companyId,
    limit = 5,
    dateFrom = null,
    dateTo = null,
    locationId = null,
  ) {
    const response = await apiClient.post("/api/entities/overview", {
      company_id: companyId,
      limit,
      date_from: dateFrom,
      date_to: dateTo,
      location_id: locationId,
    });
    return response.data;
  }

  static async create(companyId, type, label) {
    const response = await apiClient.post("/api/entity", {
      company_id: companyId,
      type,
      label,
    });
    return response.data;
  }

  static async update(entityId, companyId, type, label) {
    const response = await apiClient.patch(`/api/entity/${entityId}`, {
      company_id: companyId,
      type,
      label,
    });
    return response.data;
  }

  static async remove(entityId, companyId) {
    const response = await apiClient.delete(`/api/entity/${entityId}`, {
      data: { company_id: companyId },
    });
    return response.data;
  }

  static async merge(companyId, sourceId, targetId) {
    const response = await apiClient.post("/api/entity/merge", {
      company_id: companyId,
      source_id: sourceId,
      target_id: targetId,
    });
    return response.data;
  }
}

export class SocialInsights {
  static async getTopicsOverview(
    companyId,
    aggregateBy = "monthly",
    dateFrom = null,
    dateTo = null,
    locationId = null,
    source = "FACEBOOK",
  ) {
    try {
      const r = await apiClient.post("/api/company/social-topics", {
        company_id: companyId,
        aggregate_by: aggregateBy,
        date_from: dateFrom,
        date_to: dateTo,
        location_id: locationId,
        source,
      });
      return r.data ?? { items: [], total: 0 };
    } catch (error) {
      console.error("Error fetching social topics:", error);
      return { items: [], total: 0 };
    }
  }

  static async getTopicTrend(
    companyId,
    topic,
    aggregateBy = "monthly",
    dateFrom = null,
    dateTo = null,
    locationId = null,
    source = "FACEBOOK",
  ) {
    try {
      const r = await apiClient.post("/api/company/social-topics-trend", {
        company_id: companyId,
        topic,
        aggregate_by: aggregateBy,
        date_from: dateFrom,
        date_to: dateTo,
        location_id: locationId,
        source,
      });
      return r.data;
    } catch (error) {
      console.error("Error fetching social topic trend:", error);
      return null;
    }
  }

  static async getEntities(
    companyId,
    type,
    dateFrom = null,
    dateTo = null,
    locationId = null,
    source = "FACEBOOK",
  ) {
    try {
      const r = await apiClient.post("/api/company/social-entities", {
        company_id: companyId,
        type,
        date_from: dateFrom,
        date_to: dateTo,
        location_id: locationId,
        source,
      });
      return r.data ?? { items: [] };
    } catch (error) {
      console.error("Error fetching social entities:", error);
      return { items: [] };
    }
  }

  static async getEntityTrend(
    companyId,
    type,
    label,
    aggregateBy = "monthly",
    dateFrom = null,
    dateTo = null,
    locationId = null,
    source = "FACEBOOK",
  ) {
    try {
      const r = await apiClient.post("/api/company/social-entities-trend", {
        company_id: companyId,
        type,
        label,
        aggregate_by: aggregateBy,
        date_from: dateFrom,
        date_to: dateTo,
        location_id: locationId,
        source,
      });
      return r.data;
    } catch (error) {
      console.error("Error fetching social entity trend:", error);
      return null;
    }
  }

  static async getSentimentBreakdown(
    companyId,
    dateFrom = null,
    dateTo = null,
    locationId = null,
    source = "FACEBOOK",
  ) {
    try {
      const r = await apiClient.post(
        "/api/company/social-sentiment-breakdown",
        {
          company_id: companyId,
          date_from: dateFrom,
          date_to: dateTo,
          location_id: locationId,
          source,
        },
      );
      return (
        r.data ?? {
          total: 0,
          positive: 0,
          neutral: 0,
          negative: 0,
          untriaged: 0,
        }
      );
    } catch (error) {
      console.error("Error fetching social sentiment breakdown:", error);
      return { total: 0, positive: 0, neutral: 0, negative: 0, untriaged: 0 };
    }
  }
}

export class Facebook {
  static async storePages(accessToken) {
    const response = await apiClient.post("/api/auth/facebook/store-pages", {
      access_token: accessToken,
    });
    return response.data;
  }

  static async getSocialStats(
    companyId,
    locationId,
    dateFrom,
    dateTo,
    source = null,
    comparePrevious = true,
  ) {
    const metric = { current: 0, previous: null, change_pct: null };
    const empty = {
      totals: {},
      summary: {
        posts: { ...metric },
        engagements: { ...metric },
        engagement_per_post: { ...metric },
        audience_comments: { ...metric },
        reply_rate: { ...metric },
      },
      comparison: {
        enabled: false,
        available: false,
        date_from: null,
        date_to: null,
      },
      series: [],
      previous_series: [],
      granularity: "day",
      by_source: {},
      reaction_breakdown: {},
      top_posts: [],
      sentiment_series: [],
      response: {
        rate: 0,
        median_minutes: 0,
        responded: 0,
        eligible: 0,
      },
      response_counts: { responded: 0, eligible: 0 },
      top_voices: [],
      attention: {
        total: 0,
        new: 0,
        open: 0,
        by_urgency: {},
        by_intent: {},
        items: [],
      },
      comment_sentiment: {
        total: 0,
        positive: 0,
        neutral: 0,
        negative: 0,
        untriaged: 0,
        series: [],
      },
      topics: [],
      entities: [],
      freshness: { last_post_at: null, last_updated_at: null },
    };
    const r = await apiClient.post("/api/company/social-stats", {
      company_id: companyId,
      location_id: locationId,
      date_from: dateFrom,
      date_to: dateTo,
      source,
      compare_previous: comparePrevious,
    });
    const data = r.data ?? {};
    return {
      ...empty,
      ...data,
      summary: { ...empty.summary, ...(data.summary || {}) },
      response: { ...empty.response, ...(data.response || {}) },
      response_counts: {
        ...empty.response_counts,
        ...(data.response_counts || {}),
      },
      comparison: { ...empty.comparison, ...(data.comparison || {}) },
      attention: { ...empty.attention, ...(data.attention || {}) },
      comment_sentiment: {
        ...empty.comment_sentiment,
        ...(data.comment_sentiment || {}),
      },
      freshness: { ...empty.freshness, ...(data.freshness || {}) },
    };
  }

  static async getSocialContentPerformance(
    companyId,
    locationId,
    dateFrom,
    dateTo,
    source = null,
  ) {
    try {
      const r = await apiClient.post(
        "/api/company/social-content-performance",
        {
          company_id: companyId,
          location_id: locationId,
          date_from: dateFrom,
          date_to: dateTo,
          ...(source ? { source } : {}),
        },
      );
      return r.data ?? { themes: [] };
    } catch {
      return { themes: [] };
    }
  }
}

export class Social {
  static async getInbox(
    companyId,
    {
      intent = null,
      urgency = null,
      triageStatus = null,
      locationId = null,
      dateFrom = null,
      dateTo = null,
      source = "FACEBOOK",
    } = {},
  ) {
    try {
      const r = await apiClient.post("/api/company/social-inbox", {
        company_id: companyId,
        intent,
        urgency,
        triage_status: triageStatus,
        location_id: locationId,
        date_from: dateFrom,
        date_to: dateTo,
        source,
      });
      return r.data ?? { items: [], counts: { by_intent: {}, by_status: {} } };
    } catch {
      return { items: [], counts: { by_intent: {}, by_status: {} } };
    }
  }

  static async setTriageStatus(commentId, triageStatus) {
    try {
      const r = await apiClient.post(
        `/api/comment/${commentId}/triage-status`,
        {
          triage_status: triageStatus,
        },
      );
      return r.data;
    } catch (error) {
      console.error("Error setting triage status:", error);
      return null;
    }
  }

  static async getAuthor(authorId, companyId) {
    try {
      const r = await apiClient.post(
        `/api/social/author/${encodeURIComponent(authorId)}`,
        { company_id: companyId },
      );
      return r.data;
    } catch (error) {
      console.error("Error fetching author profile:", error);
      return null;
    }
  }
}

export class Admin {
  static async getScrapeJobs(params = {}) {
    try {
      const response = await apiClient.get("/api/admin/scrape-jobs", {
        params,
      });
      return response.data;
    } catch (error) {
      console.error("Error fetching scrape jobs:", error);
      return { items: [], total: 0, page: 1, per_page: 50 };
    }
  }

  static async getActiveJobs() {
    try {
      const response = await apiClient.get("/api/admin/scrape-jobs/active");
      return response.data;
    } catch (error) {
      console.error("Error fetching active jobs:", error);
      return [];
    }
  }

  static async getSummary() {
    try {
      const response = await apiClient.get("/api/admin/scrape-jobs/summary");
      return response.data;
    } catch (error) {
      console.error("Error fetching job summary:", error);
      return null;
    }
  }

  static async getNetworkUsageSummary(params = {}) {
    try {
      const response = await apiClient.get("/api/admin/network-usage/summary", {
        params,
      });
      return response.data;
    } catch (error) {
      console.error("Error fetching scraper network usage:", error);
      return null;
    }
  }

  static async getNetworkUsage(params = {}) {
    try {
      const response = await apiClient.get("/api/admin/network-usage", {
        params,
      });
      return response.data;
    } catch (error) {
      console.error("Error fetching scraper network usage events:", error);
      return { items: [], total: 0, page: 1, per_page: 50 };
    }
  }

  static async triggerScrape(payload) {
    try {
      const response = await apiClient.post(
        "/api/admin/scrape-jobs/trigger",
        payload,
      );
      return response.data;
    } catch (error) {
      console.error("Error triggering scrape:", error);
      throw error;
    }
  }
}

export class LlmCosts {
  static async getSummary(params = {}) {
    try {
      const response = await apiClient.get("/api/admin/llm-costs/summary", {
        params,
      });
      return response.data;
    } catch (error) {
      console.error("Error fetching LLM cost summary:", error);
      return null;
    }
  }
}

let adminOutreachCsrfToken = "";
let adminOutreachCsrfRequest = null;

async function getAdminOutreachCsrfToken() {
  if (adminOutreachCsrfToken) return adminOutreachCsrfToken;
  if (!adminOutreachCsrfRequest) {
    adminOutreachCsrfRequest = apiClient
      .get("/api/admin/outreach/csrf")
      .then((response) => {
        const token =
          response.data?.csrf_token ||
          response.data?.csrfToken ||
          response.data?.token ||
          "";
        if (!token) throw new Error("Outreach CSRF endpoint returned no token");
        adminOutreachCsrfToken = token;
        return token;
      })
      .finally(() => {
        adminOutreachCsrfRequest = null;
      });
  }
  return adminOutreachCsrfRequest;
}

function isCsrfFailure(error) {
  if (error?.response?.status !== 403) return false;
  const detail = String(
    typeof error.response.data === "string"
      ? error.response.data
      : JSON.stringify(error.response.data || {}),
  ).toLowerCase();
  return detail.includes("csrf");
}

async function postAdminOutreach(url, payload, retry = true) {
  const csrfToken = await getAdminOutreachCsrfToken();
  try {
    return await apiClient.post(url, payload, {
      headers: { "X-CSRFToken": csrfToken },
    });
  } catch (error) {
    if (!retry || !isCsrfFailure(error)) throw error;
    adminOutreachCsrfToken = "";
    return postAdminOutreach(url, payload, false);
  }
}

export class AdminOutreach {
  static async getEmails(params = {}) {
    const response = await apiClient.get("/api/admin/outreach/emails", {
      params,
    });
    return response.data;
  }

  static async getEmail(emailId) {
    const response = await apiClient.get(
      `/api/admin/outreach/emails/${emailId}`,
    );
    return response.data;
  }

  static async getSummary() {
    const response = await apiClient.get("/api/admin/outreach/summary");
    return response.data;
  }

  static async createEmail(payload) {
    const response = await postAdminOutreach(
      "/api/admin/outreach/emails",
      payload,
    );
    return response.data;
  }

  static async updateEmail(emailId, action, payload = {}) {
    const response = await postAdminOutreach(
      `/api/admin/outreach/emails/${emailId}/${action}`,
      payload,
    );
    return response.data;
  }

  static approveEmail(emailId) {
    return this.updateEmail(emailId, "approve");
  }

  static rejectEmail(emailId) {
    return this.updateEmail(emailId, "reject");
  }

  static closeEmail(emailId) {
    return this.updateEmail(emailId, "close");
  }

  static retryEmail(emailId) {
    return this.updateEmail(emailId, "retry");
  }

  static async getProspects(params = {}) {
    const response = await apiClient.get("/api/admin/outreach/prospects", {
      params,
    });
    return response.data;
  }

  static async getProspect(prospectId) {
    const response = await apiClient.get(
      `/api/admin/outreach/prospects/${prospectId}`,
    );
    return response.data;
  }

  static async generateEmailPreview(prospectId, payload = { use_llm: true }) {
    const response = await postAdminOutreach(
      `/api/admin/outreach/prospects/${prospectId}/email-preview`,
      payload,
    );
    return response.data;
  }

  static async createProspect(payload = {}) {
    const response = await postAdminOutreach(
      "/api/admin/outreach/prospects",
      payload,
    );
    return response.data;
  }

  static async updateProspect(prospectId, action, payload = {}) {
    const response = await postAdminOutreach(
      `/api/admin/outreach/prospects/${prospectId}/${action}`,
      payload,
    );
    return response.data;
  }

  static shortlistProspect(prospectId) {
    return this.updateProspect(prospectId, "shortlist");
  }

  static rejectProspect(prospectId) {
    return this.updateProspect(prospectId, "reject");
  }

  static enrichProspect(prospectId) {
    return this.updateProspect(prospectId, "enrich");
  }

  static allowWatchedCompany(prospectId) {
    return this.updateProspect(prospectId, "allow-watched-company");
  }

  static async getAnalyticsFunnel(params = {}) {
    const response = await apiClient.get(
      "/api/admin/outreach/analytics/funnel",
      { params },
    );
    return response.data;
  }

  static async getAnalyticsTimeseries(params = {}) {
    const response = await apiClient.get(
      "/api/admin/outreach/analytics/timeseries",
      { params },
    );
    return response.data;
  }

  static async getAnalyticsCampaigns(params = {}) {
    const response = await apiClient.get(
      "/api/admin/outreach/analytics/campaigns",
      { params },
    );
    return response.data;
  }
}

export class Onboarding {
  static async getIndustries() {
    const response = await apiClient.get("/api/industries");
    return response.data;
  }

  // First-import progress for the org's currently-importing company. Returns
  // { importing: false } when nothing is importing. Polled by the dashboard
  // import banner.
  static async importStatus() {
    const response = await apiClient.get("/api/import-status");
    return response.data;
  }

  static async research(companyName) {
    const response = await apiClient.post("/api/admin/onboard/research", {
      company_name: companyName,
    });
    return response.data;
  }

  // Public research for the self-service onboarding wizard (no auth required —
  // it runs before an account exists).
  static async researchSelfService(companyName) {
    const response = await apiClient.post("/api/onboard/research", {
      company_name: companyName,
    });
    return response.data;
  }

  // Step one of the two-step wizard research: identify the company so the user
  // can confirm it's correct before we look for channels.
  static async researchCompany(companyName) {
    const response = await apiClient.post("/api/onboard/research/company", {
      company_name: companyName,
    });
    return response.data;
  }

  // Step two: once the company is confirmed, find its review channels, products
  // and competitors. `biz` carries the confirmed name/website/description.
  static async researchChannels(biz) {
    const response = await apiClient.post("/api/onboard/research/channels", {
      company_name: biz.name,
      website: biz.website || "",
      description: biz.desc || "",
    });
    return response.data;
  }

  static async create(payload) {
    const response = await apiClient.post("/api/admin/onboard/create", payload);
    return response.data;
  }

  // Admin "company only" mode: create a standalone company + channels with no
  // organisation, user or competitors.
  static async createCompany(payload) {
    const response = await apiClient.post(
      "/api/admin/onboard/company",
      payload,
    );
    return response.data;
  }

  // Public self-service create: creates the account + workspace and logs the
  // new user in.
  static async createSelfService(payload) {
    const response = await apiClient.post("/api/onboard/create", payload);
    return response.data;
  }

  // Build the workspace for the already-authenticated user (account is created
  // up front at step 1 via Auth.signup).
  static async createWorkspace(payload) {
    const response = await apiClient.post("/api/onboard/workspace", payload);
    return response.data;
  }

  // Public onboarding config (e.g. whether the trial requires a card).
  static async getConfig() {
    const response = await apiClient.get("/api/onboard/config");
    return response.data;
  }

  // Record one onboarding funnel event. Best-effort: callers fire-and-forget.
  static track(payload) {
    return apiClient.post("/api/onboard/track", payload);
  }

  static async paystackInitialize({ plan, period }) {
    const response = await apiClient.post("/api/paystack/initialize", {
      plan,
      period,
    });
    return response.data;
  }

  static async paystackVerify(payload) {
    const response = await apiClient.post("/api/paystack/verify", payload);
    return response.data;
  }
}

// Admin rediscover-sources: re-run discovery for an existing company and add
// any newly-found channels.
export class AdminCompanies {
  static async search(query) {
    const response = await apiClient.get("/api/admin/companies", {
      params: { q: query || "" },
    });
    return response.data;
  }

  static async rediscover(companyId) {
    const response = await apiClient.post(
      `/api/admin/company/${companyId}/rediscover`,
    );
    return response.data;
  }

  static async addChannels(companyId, payload) {
    const response = await apiClient.post(
      `/api/admin/company/${companyId}/channels`,
      payload,
    );
    return response.data;
  }
}

export class Insights {
  static async get(
    companyId,
    dateFrom,
    dateTo,
    locationId = null,
    limit = null,
  ) {
    try {
      const response = await apiClient.post(`/api/company/insights`, {
        company_id: companyId,
        date_from: dateFrom,
        date_to: dateTo,
        location_id: locationId,
        limit: limit,
      });
      return response.data;
    } catch (error) {
      console.error("Error fetching insights:", error);
      return { insights: [], has_more: false };
    }
  }

  static async setState(companyId, kind, subject, action) {
    try {
      const response = await apiClient.post(`/api/company/insight-state`, {
        company_id: companyId,
        kind: kind,
        subject: subject,
        action: action,
      });
      return response.data;
    } catch (error) {
      console.error("Error updating insight state:", error);
      return { ok: false };
    }
  }
}

export class AI {
  static async generateDraftReply(companyId, reviewId, tone) {
    const response = await apiClient.post("/api/review/draft-reply", {
      company_id:
        typeof companyId === "string" ? parseInt(companyId) : companyId,
      review_id: typeof reviewId === "string" ? parseInt(reviewId) : reviewId,
      tone,
    });
    // Demo-blocked requests are resolved by the interceptor with data === null.
    return response.data;
  }

  static async generateCommentReply(companyId, commentId, tone) {
    const response = await apiClient.post(
      `/api/comment/${typeof commentId === "string" ? parseInt(commentId) : commentId}/draft-reply`,
      {
        company_id:
          typeof companyId === "string" ? parseInt(companyId) : companyId,
        tone,
      },
    );
    // Demo-blocked requests are resolved by the interceptor with data === null.
    return response.data;
  }
}
